
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useReferralSystem } from "@/hooks/use-referral-system";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line } from "recharts";
import { format } from "date-fns";

export const ReferralAnalytics = () => {
  const { analytics, isLoadingAnalytics } = useReferralSystem();

  if (isLoadingAnalytics) {
    return (
      <div className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>Loading Analytics...</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="animate-pulse space-y-4">
              <div className="h-64 bg-gray-200 rounded"></div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!analytics || analytics.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Referral Analytics</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8">
            <p className="text-gray-600">No analytics data available yet.</p>
            <p className="text-sm text-gray-500 mt-2">
              Start sharing your referral link to see performance metrics here.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Prepare chart data
  const chartData = analytics.map(month => ({
    month: format(new Date(month.month_year), 'MMM yyyy'),
    clicks: month.total_clicks,
    conversions: month.total_conversions,
    commission: month.total_commission,
    conversionRate: month.conversion_rate * 100,
  })).reverse(); // Show oldest first

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Monthly Performance</CardTitle>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" />
              <YAxis />
              <Tooltip />
              <Bar dataKey="clicks" fill="#3b82f6" name="Clicks" />
              <Bar dataKey="conversions" fill="#10b981" name="Conversions" />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Commission Earnings</CardTitle>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" />
              <YAxis />
              <Tooltip formatter={(value) => [`$${value}`, 'Commission']} />
              <Line 
                type="monotone" 
                dataKey="commission" 
                stroke="#10b981" 
                strokeWidth={2}
                dot={{ fill: '#10b981' }}
              />
            </LineChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Conversion Rate Trend</CardTitle>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" />
              <YAxis />
              <Tooltip formatter={(value) => [`${value}%`, 'Conversion Rate']} />
              <Line 
                type="monotone" 
                dataKey="conversionRate" 
                stroke="#8b5cf6" 
                strokeWidth={2}
                dot={{ fill: '#8b5cf6' }}
              />
            </LineChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {analytics.slice(0, 3).map((month) => (
          <Card key={month.id}>
            <CardHeader>
              <CardTitle className="text-lg">
                {format(new Date(month.month_year), 'MMMM yyyy')}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Clicks:</span>
                <span className="font-semibold">{month.total_clicks}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Conversions:</span>
                <span className="font-semibold">{month.total_conversions}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Commission:</span>
                <span className="font-semibold text-green-600">
                  ${month.total_commission.toFixed(2)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Conversion Rate:</span>
                <span className="font-semibold">
                  {(month.conversion_rate * 100).toFixed(1)}%
                </span>
              </div>
              {month.top_referrer_bonus > 0 && (
                <div className="flex justify-between pt-2 border-t">
                  <span className="text-sm text-amber-600">Bonus:</span>
                  <span className="font-semibold text-amber-600">
                    ${month.top_referrer_bonus.toFixed(2)}
                  </span>
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};
